import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';

import { Badge, Card, Icon, KpiCard } from '@ds/core';
import { Alert } from '@ds/feedback';
import { PageHeader } from '@/comuns/PageHeader';
import { dados } from '@/data/porta';
import { temSupabase } from '@/supabase';
import type { CargaDaPlataforma, SaudeDoBanco } from '@/data/tipos';

function tamanho(bytes: number): string {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('pt-BR')} KB`;
}

interface Medida {
    rotulo: string;
    ms?: number;
    erro?: string;
}

/**
 * Saúde do sistema — medida daqui, e só o que daqui se mede.
 *
 * O navegador não enxerga CPU, memória nem fila: quem tem isso é o painel da
 * infraestrutura. O que dá para medir com honestidade é **se o banco responde e
 * em quanto tempo**, e é isso que a tela faz — uma consulta de verdade,
 * cronometrada agora, em vez de um "tudo operacional" verde que ninguém
 * calculou.
 */
export function Saude() {
    const carga = useLoaderData() as CargaDaPlataforma;
    const [medidas, setMedidas] = useState<Medida[] | null>(null);
    /* `undefined` é "medindo"; `null` é "não há banco para medir". */
    const [banco, setBanco] = useState<SaudeDoBanco | null | undefined>(undefined);
    const [erroDoBanco, setErroDoBanco] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        dados().saudeDoBanco()
            .then((s) => { if (vivo) setBanco(s); })
            .catch((e: unknown) => { if (vivo) setErroDoBanco(e instanceof Error ? e.message : 'Não foi possível medir o banco.'); });
        return () => { vivo = false; };
    }, []);

    useEffect(() => {
        let vivo = true;
        const medir = async () => {
            /* Só o que o operador lê. A segunda medida era "Leitura de clínica",
               e o operador não tem clínica na sessão — a tela de saúde exibia, a
               todo operador, "Sessão sem clínica: entre novamente." como se o
               sistema estivesse doente. As flags são leitura da plataforma, com
               política própria: medem o mesmo caminho (PostgREST + RLS) sem fingir
               um erro. */
            const testes: { rotulo: string; executar: () => Promise<unknown> }[] = [
                { rotulo: 'Leitura do painel', executar: () => dados().plataforma() },
                { rotulo: 'Leitura das flags de módulo', executar: () => dados().flags() },
            ];
            const resultado: Medida[] = [];
            for (const t of testes) {
                const inicio = performance.now();
                try {
                    await t.executar();
                    resultado.push({ rotulo: t.rotulo, ms: Math.round(performance.now() - inicio) });
                } catch (e) {
                    resultado.push({ rotulo: t.rotulo, erro: e instanceof Error ? e.message : 'falhou' });
                }
            }
            if (vivo) setMedidas(resultado);
        };
        void medir();
        return () => { vivo = false; };
    }, []);

    const impersonacoesVivas = carga.impersonacoes.filter((i) => i.viva).length;

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader title="Saúde do sistema" description="O que dá para medir do navegador, medido agora." />

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard
                    label="Origem dos dados" value={temSupabase() ? 'Servidor' : 'Demonstração'}
                    hint={temSupabase() ? 'PostgREST + GoTrue' : 'adaptador em memória'}
                    icon={<Icon name="database" size={16} />}
                />
                <KpiCard label="Clínicas na base" value={String(carga.clinicas.length)} hint="não arquivadas" icon={<Icon name="building-2" size={16} />} />
                <KpiCard label="Contas com acesso" value={String(carga.usuarios.length)} hint="em todas as clínicas" icon={<Icon name="users" size={16} />} />
                <KpiCard
                    label="Impersonações abertas" value={String(impersonacoesVivas)}
                    hint={impersonacoesVivas ? 'alguém está dentro de uma clínica' : 'ninguém dentro de clínica'}
                    deltaTone={impersonacoesVivas ? 'down' : 'up'}
                    icon={<Icon name="eye" size={16} />}
                />
            </div>

            <Card title="Tempo de resposta" subtitle="Consultas reais, cronometradas ao abrir esta tela" padding={18}>
                {medidas === null ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Medindo…</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {medidas.map((m) => (
                            <div key={m.rotulo} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                                <span style={{ flex: '1 1 10rem', minWidth: 0 }}>{m.rotulo}</span>
                                {m.erro
                                    ? <Badge tone="danger">{m.erro}</Badge>
                                    : (
                                        <>
                                            <Badge tone={(m.ms ?? 0) < 400 ? 'success' : (m.ms ?? 0) < 1200 ? 'warning' : 'danger'}>
                                                {(m.ms ?? 0) < 400 ? 'rápido' : (m.ms ?? 0) < 1200 ? 'lento' : 'muito lento'}
                                            </Badge>
                                            <span style={{ width: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                                                {m.ms} ms
                                            </span>
                                        </>
                                    )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card title="Banco de dados" subtitle="O que o Postgres mede de si mesmo, agora" padding={18}>
                {erroDoBanco && <Alert tone="danger" dismissible={false}>{erroDoBanco}</Alert>}
                {!erroDoBanco && banco === undefined && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Medindo…</p>
                )}
                {!erroDoBanco && banco === null && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                        A demonstração não tem banco para medir: conexões, tamanho e agendador só existem com o servidor.
                    </p>
                )}
                {!erroDoBanco && banco && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                            <KpiCard
                                label="Conexões" value={`${banco.conexoes} de ${banco.maxConexoes}`}
                                hint={`${banco.conexoesAtivas} executando agora`}
                                deltaTone={banco.conexoes / Math.max(1, banco.maxConexoes) > 0.8 ? 'down' : 'neutral'}
                                icon={<Icon name="plug" size={16} />}
                            />
                            <KpiCard label="Tamanho do banco" value={tamanho(banco.tamanhoBytes)} hint={`Postgres ${banco.versao}`} icon={<Icon name="database" size={16} />} />
                            <KpiCard
                                label="No ar desde"
                                value={banco.iniciadoEm ? new Date(banco.iniciadoEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'}
                                hint="último reinício do banco" icon={<Icon name="clock" size={16} />}
                            />
                            <KpiCard
                                label="Agendador"
                                value={banco.agendador ? `${banco.agendador.falhas24h} ${banco.agendador.falhas24h === 1 ? 'falha' : 'falhas'}` : 'não instalado'}
                                hint={banco.agendador
                                    ? `nas últimas 24 h${banco.agendador.ultimaExecucaoEm
                                        ? ` · última rodada ${new Date(banco.agendador.ultimaExecucaoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}`
                                        : ' · nenhuma rodada ainda'}`
                                    : 'nenhum agendador instalado nesta base'}
                                deltaTone={banco.agendador && banco.agendador.falhas24h > 0 ? 'down' : 'neutral'}
                                icon={<Icon name="calendar-clock" size={16} />}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <strong style={{ fontSize: 12.5, fontWeight: 600 }}>Maiores tabelas</strong>
                            {banco.maioresTabelas.map((t) => (
                                <div key={t.tabela} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                    <code>{t.tabela}</code>
                                    <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{tamanho(t.bytes)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            <Alert tone="info" dismissible={false} title="CPU, memória e filas não passam por aqui">
                O banco mede conexões e tamanho — é o que está acima. CPU, memória e disco da máquina são da
                infraestrutura, e nem o navegador nem o Postgres os alcançam: quem os tem é o painel de quem
                hospeda. Inventar um semáforo verde nesta tela seria dizer que o sistema está bem sem ter olhado.
            </Alert>
        </div>
    );
}
